import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useForm, Controller, useFieldArray} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {useNavigate, useParams} from "react-router-dom";
import {Col, Row, Card, Space, Button as AntButton, notification, Select, Switch, Divider, Radio, Modal, Input, InputNumber} from "antd";
import {DeleteOutlined, PlusOutlined, CalculatorOutlined} from "@ant-design/icons";
import {
	Button,
	Loading
} from "~/components";
import {
	InputField,
	SelectField,
	TextAreaField,
	InputPriceField,
} from "~/components/Forms";
import ActionBar from "~/layout/ActionBar";
import {handleRequest, apiError} from "~/utils";
import {productApi, productCategoryApi, attributeApi} from "~/api";
import {AppContext} from "../../../context/AppProvider";

function ProductFormAddEdit()
{
	const {appData} = useContext(AppContext);

	const {id} = useParams();

	const isEdit = Boolean(id);

	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);

	const [productCategories, setProductCategories] = useState([]);

	const [attributes, setAttributes] = useState([]);

	const [hasVariation, setHasVariation] = useState(false);

	const [showPriceCalculator, setShowPriceCalculator] = useState(false);
	const [calculatorWeight, setCalculatorWeight] = useState('');
	const [calculatorPrice, setCalculatorPrice] = useState('');
	const [calculatorResult, setCalculatorResult] = useState(null);
	const [calculatorFieldName, setCalculatorFieldName] = useState('price'); // Track which field to update

	// Auto-calculate when weight or price changes
	useEffect(() => {
		if (!calculatorWeight || !calculatorPrice) {
			setCalculatorResult(null);
			return;
		}

		let weightInGrams = parseFloat(calculatorWeight);

		// Convert kg to grams if needed
		if (calculatorWeight.toString().includes('kg') || calculatorWeight.toString().includes('k')) {
			weightInGrams = parseFloat(calculatorWeight) * 1000;
		}

		if (weightInGrams > 0) {
			const price = parseFloat(calculatorPrice);
			const price100g = (price * 100) / weightInGrams;

			setCalculatorResult({
				weight: weightInGrams,
				price: price,
				price100g: price100g.toFixed(0)
			});
		}
	}, [calculatorWeight, calculatorPrice]);

	const unitOptions = Object.values(appData['units']).map(item => ({
		value: item.value,
		label: item.label
	}));

	const initialValues = useMemo(
		() => ({
			title: "",
			code: "",
			excerpt: "",
			image: "",
			price: 0,
			vat: 0,
			stock: 0,
			unit: "item",
			status: "public",
			categoryIds: [],
			hasVariation: 0,
			selectedAttributes: [],
			variations: [],
		}),
		[]
	);

	const validationSchema = Yup.object().shape({
		title: Yup.string().required('Tên sản phẩm không được bỏ trống'),
		code: Yup.string(),
		price: Yup.number().min(0, 'Giá bán phải lớn hơn hoặc bằng 0'),
		vat: Yup.number().min(0, 'Thuế phải lớn hơn hoặc bằng 0').max(100, 'Thuế không được vượt quá 100%'),
		stock: Yup.number().min(0, 'Số lượng tồn kho phải lớn hơn hoặc bằng 0'),
		unit: Yup.string().required('Đơn vị tính không được bỏ trống'),
	})

	const {control, handleSubmit, formState: {isSubmitting, errors}, reset, setValue, watch} = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(validationSchema)
	});

	const unit = watch('unit');
	const getPriceLabel = () => {
		return unit === 'kg' ? 'Giá bán (0.1 kg)' : 'Giá bán';
	};


	const { fields, append, remove } = useFieldArray({
		control,
		name: "variations"
	});

	const watchHasVariation = watch("hasVariation");
	const watchSelectedAttributes = watch("selectedAttributes");

	// Filter attributes based on selectedAttributes
	// Only show attributes when user has selected attribute groups
	const filteredAttributes = useMemo(() => {
		// Nếu chưa chọn nhóm thuộc tính → không hiển thị gì
		if (!watchSelectedAttributes || watchSelectedAttributes.length === 0) {
			return [];
		}
		// Nếu đã chọn nhóm thuộc tính → chỉ hiển thị những cái được chọn
		return attributes.filter(attr => watchSelectedAttributes.includes(attr.id));
	}, [attributes, watchSelectedAttributes]);

	useEffect(() => {
		setHasVariation(watchHasVariation === 1);
	}, [watchHasVariation]);

	// Auto manage variation rows to surface attribute selectors immediately
	useEffect(() => {
		if (hasVariation && fields.length === 0)
		{
			addVariation();
		}

		if (!hasVariation && fields.length > 0)
		{
			remove();
		}
	}, [hasVariation]);

	// Load product categories
	const loadProductCategories = useCallback(async () => {
		let [error, response] = await handleRequest(productCategoryApi.options());
		if(!apiError('Lấy danh sách danh mục thất bại', error, response)) {
			// Convert object to array if needed
			let categories = response.data || [];
			if (!Array.isArray(categories)) {
				// If it's an object, convert to array
				categories = Object.entries(categories).map(([key, value]) => ({
					id: parseInt(key),
					name: value
				}));
			}
			setProductCategories(categories);
		}
	}, []);

	// Load attributes
	const loadAttributes = useCallback(async () => {
		let [error, response] = await handleRequest(attributeApi.options());
		if(!apiError('Lấy danh sách thuộc tính thất bại', error, response)) {
			const attrs = response.data?.data || response.data || [];
			setAttributes(Array.isArray(attrs) ? attrs : []);
		}
	}, []);

	const goToAttributePage = useCallback(() => {
		navigate('/attribute');
	}, [navigate]);

	// Load product data for edit
	const loadProduct = useCallback(async () =>
	{
		if (!id) return;

		setLoading(true);
		let [error, response] = await handleRequest(productApi.get(id));
		setLoading(false);

		if(apiError('Lấy thông tin sản phẩm thất bại', error, response))
		{
			navigate('/product');
			return;
		}

		const productData = response.data;

		// Ensure variations have is_default field
		const variations = (productData.variations || []).map((variation, index) => ({
			...variation,
			is_default: variation.is_default !== undefined ? variation.is_default : false,
			stock: variation.stock !== undefined ? variation.stock : 0
		}));

		reset({
			title: productData.title || "",
			code: productData.code || "",
			excerpt: productData.excerpt || "",
			image: productData.image || "",
			price: productData.price || 0,
			vat: productData.vat || 0,
			stock: productData.stock || 0,
			unit: productData.unit || "item",
			status: productData.status || "public",
			categoryIds: productData.categoryIds || [],
			hasVariation: productData.hasVariation || 0,
			selectedAttributes: productData.selectedAttributes || [],
			variations: variations,
		});

		setHasVariation(productData.hasVariation === 1);
	}, [id, navigate, reset]);

	useEffect(() => {
		loadProductCategories();
		loadAttributes();
	}, [loadProductCategories, loadAttributes]);

	useEffect(() => {
		if (isEdit) {
			loadProduct();
		} else {
			// Reset form when adding new product
			reset({
				title: "",
				code: "",
				excerpt: "",
				image: "",
				price: 0,
				vat: 0,
				stock: 0,
				unit: "item",
				status: "public",
				categoryIds: [],
				hasVariation: 0,
				selectedAttributes: [],
				variations: [],
			});
		}
	}, [isEdit, loadProduct, reset]);

	const onSubmit = async (data) => {
		const submitData = {...data};

		if (isEdit) {
			submitData.id = id;
			let [error, response] = await handleRequest(productApi.update(submitData));

			if(!apiError('Cập nhật sản phẩm thất bại', error, response)) {
				notification.success({message: 'Thành công', description: 'Cập nhật sản phẩm thành công'});
				navigate('/product');
			}
		} else {
			let [error, response] = await handleRequest(productApi.add(submitData));

			if(!apiError('Thêm sản phẩm thất bại', error, response)) {
				notification.success({message: 'Thành công', description: 'Thêm sản phẩm thành công'});
				navigate('/product');
			}
		}
	};

	const addVariation = () => {
		const isFirstVariation = fields.length === 0;
		append({
			title: "",
			code: "",
			price: 0,
			vat: 0,
			stock: 0,
			unit: "item",
			image: "",
			status: "public",
			attributes: [],
			is_default: isFirstVariation // First variation is default
		});
	};

	if (loading) {
		return <Loading />;
	}

	return (
		<>
			<form onSubmit={handleSubmit(onSubmit)} className={'form'}>
				<ActionBar title={isEdit ? 'Cập nhật sản phẩm' : 'Thêm sản phẩm'}>
					<Button background blue type="submit" disabled={isSubmitting}>
						{isEdit ? 'Cập nhật' : 'Thêm mới'}
					</Button>
					<Button outline onClick={() => navigate('/product')}>Quay lại</Button>
				</ActionBar>
				<div className="container">
					<div className="content p-2">
						<Row gutter={16}>
							<Col span={16}>
								<Card title="Thông tin cơ bản" className="mb-3">
									<Row gutter={10}>
										<Col span={12}>
											<Controller control={control} name="title" render={({ field }) => (
												<InputField
													label="Tên sản phẩm"
													errors={errors}
													{...field}
												/>
											)} />
										</Col>
										<Col span={12}>
											<Controller control={control} name="code" render={({ field }) => (
												<InputField
													label="Mã sản phẩm"
													errors={errors}
													{...field}
												/>
											)} />
										</Col>
										<Col span={24}>
											<Controller control={control} name="excerpt" render={({ field }) => (
												<TextAreaField
													label="Mô tả"
													rows={4}
													errors={errors}
													{...field}
												/>
											)} />
										</Col>
									</Row>
								</Card>

								{/* Product Variations */}
								<Card title={
									<Space>
										<span>Biến thể sản phẩm</span>
										<Controller
											control={control}
											name="hasVariation"
											render={({ field }) => (
												<Switch
													checked={field.value === 1}
													onChange={(checked) => field.onChange(checked ? 1 : 0)}
												/>
											)}
										/>
									</Space>
								} className="mb-3">
									{hasVariation && (
										<>
											{/* Attribute Selector */}
											<div className="form-group mb-3">
												<label className="form-label">Chọn nhóm thuộc tính sử dụng</label>
												<Controller
													control={control}
													name="selectedAttributes"
													render={({ field }) => (
														<Select
															mode="multiple"
															style={{width: '100%'}}
															placeholder="Chọn nhóm thuộc tính cho biến thể"
															value={field.value}
															onChange={field.onChange}
															options={attributes.map(attr => ({
																value: attr.id,
																label: attr.name
															}))}
														/>
													)}
												/>
												{attributes.length === 0 && (
													<div style={{marginTop: '8px'}}>
														<small className="text-muted">
															Chưa có thuộc tính nào.{' '}
															<AntButton type="link" size="small" onClick={goToAttributePage}>
																Tạo thuộc tính
															</AntButton>
														</small>
													</div>
												)}
											</div>

											<Space direction="vertical" style={{width: '100%'}}>
												{fields.map((field, index) => (
													<Card
														key={field.id}
														size="small"
														title={
															<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginRight: '8px'}}>
																<span style={{fontSize: '14px', fontWeight: '500'}}>Biến thể {index + 1}</span>
																<Controller
																	control={control}
																	name={`variations.${index}.is_default`}
																	render={({ field: radioField }) => (
																		<Radio
																			checked={radioField.value === true}
																			onChange={(e) => {
																				// Uncheck all other variations
																				fields.forEach((_, i) => {
																					setValue(`variations.${i}.is_default`, i === index);
																				});
																			}}
																			style={{marginLeft: 'auto'}}
																		>
																			Mặc định
																		</Radio>
																	)}
																/>
															</div>
														}
														extra={
															<AntButton
																type="text"
																danger
																icon={<DeleteOutlined />}
																onClick={() => remove(index)}
															/>
														}
													>
														<Row gutter={10}>
															{/* Grouped Attributes Section */}
															<Col span={24}>
																<Card
																	type="inner"
																	title="Thuộc tính biến thể"
																	size="small"
																	style={{
																		marginBottom: '16px',
																		backgroundColor: '#f6f9fc',
																		border: '1px solid #d9e2ec'
																	}}
																>
																	<Controller
																		control={control}
																		name={`variations.${index}.attributes`}
																		render={({ field: attrField }) => (
																			<Space direction="vertical" style={{width: '100%'}} size="middle">
																				{filteredAttributes.length === 0 && (
																					<div style={{padding: '10px', background: '#fff3cd', borderRadius: '4px', color: '#856404'}}>
																						Vui lòng chọn nhóm thuộc tính ở trên
																					</div>
																				)}
																				{filteredAttributes.map((attr) => (
																					<div key={attr.id} style={{width: '100%'}} className={'d-flex align-items-center'}>
																						<label style={{
																							display: 'block',
																							marginBottom: '4px',
																							fontSize: '13px',
																							fontWeight: '500',
																							color: '#495057',
																							width: '150px',
																							minWidth: '150px'
																						}}>
																							{attr.name}:
																						</label>
																						<Select
																							style={{width: '100%'}}
																							placeholder={`Chọn ${attr.name}`}
																							value={attrField.value?.find(a => a.attribute_id === attr.id)?.item_id}
																							onChange={(value) => {
																								const newAttrs = attrField.value ? [...attrField.value] : [];
																								const existingIndex = newAttrs.findIndex(a => a.attribute_id === attr.id);
																								if (existingIndex >= 0) {
																									if (value === undefined || value === null) {
																										newAttrs.splice(existingIndex, 1);
																									} else {
																										newAttrs[existingIndex] = {
																											attribute_id: attr.id,
																											item_id: value
																										};
																									}
																								} else {
																									if (value !== undefined && value !== null) {
																										newAttrs.push({
																											attribute_id: attr.id,
																											item_id: value
																										});
																									}
																								}
																								attrField.onChange(newAttrs);
																							}}
																							allowClear
																							showSearch
																							optionFilterProp="children"
																						>
																							{attr.items?.map(item => (
																								<Select.Option key={item.id} value={item.id}>
																									{item.name}
																								</Select.Option>
																							))}
																						</Select>
																					</div>
																				))}
																			</Space>
																		)}
																	/>
																</Card>
															</Col>
															<Col span={12}>
																<Controller
																	control={control}
																	name={`variations.${index}.code`}
																	render={({ field }) => (
																		<InputField
																			label="Mã biến thể"
																			{...field}
																		/>
																	)}
																/>
															</Col>
															<Col span={12}>
																<Controller
																	control={control}
																	name={`variations.${index}.unit`}
																	render={({ field }) => (
																		<SelectField
																			label="Đơn vị tính"
																			options={unitOptions}
																			{...field}
																		/>
																	)}
																/>
															</Col>
															<Col span={12}>
																<Controller
																	control={control}
																	name={`variations.${index}.price`}
																	render={({ field }) => {
																		const variationUnit = watch(`variations.${index}.unit`);
																		const variationPriceLabel = variationUnit === 'kg' ? 'Giá bán (0.1 kg)' : 'Giá bán';
																		return (
																			<>
																				<div style={{display: 'flex', alignItems: 'flex-end', gap: '4px'}}>
																					<div style={{flex: 1}}>
																						<InputPriceField
																							label={variationPriceLabel}
																							{...field}
																						/>
																					</div>
														<Button
															type={"button"}
															background
															primary
															leftIcon={<CalculatorOutlined />}
															onClick={() => {
																setCalculatorFieldName(`variations.${index}.price`);  // Set to variation price
																setShowPriceCalculator(true);
																setCalculatorWeight('');
																setCalculatorPrice('');
																setCalculatorResult(null);
															}}
															title="Tính giá 100g"
															style={{marginBottom: '8px'}}
														>
															100g
														</Button>
																				</div>
																			</>
																		);
																	}}
																/>
															</Col>
															<Col span={6}>
																<Controller
																	control={control}
																	name={`variations.${index}.vat`}
																	render={({ field }) => (
																		<InputField
																			label="Thuế (%)"
																			type="number"
																			{...field}
																		/>
																	)}
																/>
															</Col>
															<Col span={6}>
																<Controller
																	control={control}
																	name={`variations.${index}.stock`}
																	render={({ field }) => (
																		<InputField
																			label="Tồn kho"
																			type="number"
																			{...field}
																		/>
																	)}
																/>
															</Col>
														</Row>
													</Card>
												))}
											</Space>
											<Divider />
											<AntButton
												type="dashed"
												onClick={addVariation}
												block
												icon={<PlusOutlined />}
											>
												Thêm biến thể
											</AntButton>
										</>
									)}
									{!hasVariation && (
										<p className="text-muted">Bật công tắc để thêm biến thể sản phẩm</p>
									)}
								</Card>
							</Col>

							<Col span={8}>

								<Card title="Danh mục" className="mb-3">
									<Row gutter={10}>
										<Col span={24}>
											<Controller control={control} name="categoryIds" render={({ field }) => (
												<SelectField
													label="Danh mục sản phẩm"
													mode="multiple"
													options={productCategories.map(cat => ({
														value: cat.id,
														label: cat.name
													}))}
													errors={errors}
													{...field}
												/>
											)} />
										</Col>
									</Row>
								</Card>

								<Card title="Giá và thuế" className="mb-3">
									<Row gutter={10}>
										<Col span={24}>
											<label className="form-label">Đơn vị tính</label>
											<Row gutter={8} style={{alignItems: 'flex-end'}}>
												<Col flex="auto">
													<Controller control={control} name="unit" render={({ field }) => (
														<SelectField
															label={null}
															options={unitOptions}
															errors={errors}
															{...field}
														/>
													)} />
												</Col>
											</Row>
										</Col>
										<Col span={24}>
											<label className="form-label">{getPriceLabel()}</label>
											<Row gutter={8}>
												<Col flex="auto">
													<Controller control={control} name="price" render={({ field }) => (
														<InputPriceField
															label={null}
															errors={errors}
															{...field}
														/>
													)} />
												</Col>
												<Col>
													<Button
														type="button"
														background
														primary
														leftIcon={<CalculatorOutlined />}
														onClick={() => {
															setCalculatorFieldName('price');  // Set to main product price
															setShowPriceCalculator(true);
															setCalculatorWeight('');
															setCalculatorPrice('');
															setCalculatorResult(null);
														}}
														title="Tính giá 100g"
													>
														Tính 100g
													</Button>
												</Col>
											</Row>
										</Col>
										<Col span={24}>
											<Controller control={control} name="vat" render={({ field }) => (
												<InputField
													label="Thuế (%)"
													type="number"
													errors={errors}
													{...field}
												/>
											)} />
										</Col>
										<Col span={24}>
											<Controller control={control} name="stock" render={({ field }) => (
												<InputField
													label="Số lượng tồn kho"
													type="number"
													errors={errors}
													{...field}
												/>
											)} />
										</Col>
									</Row>
								</Card>

								<Card>
									<Space>
										<Button background blue type="submit" disabled={isSubmitting}>
											{isEdit ? 'Cập nhật' : 'Thêm mới'}
										</Button>
										<Button outline onClick={() => navigate('/product')}>Hủy</Button>
									</Space>
								</Card>
							</Col>
						</Row>
					</div>
				</div>
			</form>

			{/* Price 100g Calculator Modal */}
			<Modal
				title="Tính giá 100g"
				open={showPriceCalculator}
				onCancel={() => setShowPriceCalculator(false)}
				footer={null}
				width={500}
			>
				<div style={{padding: '20px 0'}}>
					<Row gutter={16} style={{marginBottom: '16px'}}>
						<Col span={24}>
							<label className="form-label mb-1 d-block">Trọng lượng (ví dụ: 150, 150g)</label>
							<Input
								placeholder="Ví dụ: 150"
								value={calculatorWeight}
								onChange={(e) => setCalculatorWeight(e.target.value)}
								size="large"
								autoFocus
							/>
						</Col>
					</Row>

					<Row gutter={16} style={{marginBottom: '16px'}}>
						<Col span={24}>
							<label className="form-label mb-1 d-block">Giá của trọng lượng trên (VNĐ)</label>
							<InputNumber
								placeholder="Ví dụ: 100000"
								value={calculatorPrice ? parseInt(calculatorPrice) : ''}
								onChange={(value) => setCalculatorPrice(value)}
								min={0}
								step={1000}
								size="large"
								style={{width: '100%'}}
								formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
								parser={(value) => parseInt(value?.replace(/\$\s?|(,*)/g, ''))}
							/>
						</Col>
					</Row>

					{calculatorResult && (
						<Card
							style={{backgroundColor: '#f0f5ff', border: '2px solid #1890ff'}}
						>
							<Row gutter={16}>
								<Col span={24}>
									<div style={{marginBottom: '12px'}}>
										<span style={{color: '#666'}}>Trọng lượng: </span>
										<strong style={{fontSize: '16px', color: '#1890ff'}}>
											{calculatorResult.weight}g
										</strong>
									</div>
									<div style={{marginBottom: '12px'}}>
										<span style={{color: '#666'}}>Giá: </span>
										<strong style={{fontSize: '16px', color: '#1890ff'}}>
											{parseInt(calculatorResult.price).toLocaleString('vi-VN')} đ
										</strong>
									</div>
									<Divider style={{margin: '16px 0'}} />
									<div style={{textAlign: 'center'}}>
										<span style={{color: '#666', fontSize: '14px'}}>Giá của 100g là:</span>
										<div style={{
											fontSize: '28px',
											fontWeight: 'bold',
											color: '#52c41a',
											marginTop: '8px'
										}}>
											{parseInt(calculatorResult.price100g).toLocaleString('vi-VN')} đ
										</div>
									</div>
									<Row gutter={8} style={{marginTop: '16px'}}>
										<Col span={12}>
											<Button
												background
												white
												type="button"
												onClick={() => setShowPriceCalculator(false)}
												style={{width: '100%'}}
											>Đóng</Button>
										</Col>
										<Col span={12}>
											<Button
												background
												primary
												type="button"
												onClick={() => {
													setValue(calculatorFieldName, parseInt(calculatorResult.price100g));
													setShowPriceCalculator(false);
													notification.success({
														message: 'Thành công',
														description: `Giá 100g: ${parseInt(calculatorResult.price100g).toLocaleString('vi-VN')} đ`
													});
												}}
												style={{width: '100%'}}
											>
												Sử dụng giá này
											</Button>
										</Col>
									</Row>
								</Col>
							</Row>
						</Card>
					)}
				</div>
			</Modal>
		</>
	);
}

export default ProductFormAddEdit;
